import { Node, mergeAttributes } from '@tiptap/core'
import { ReactNodeViewRenderer } from '@tiptap/react'
import { MiniTopic } from '@deepguide-ai/types'
import { FolderContentsNodeView } from './folder-contents-node-view'
import { nanoid } from 'nanoid'

export interface FolderContentsAttributes {
  id?: string
  layout?: 'grid-2' | 'grid-3' | 'list'
  showIcons?: boolean
  showDescriptions?: boolean
  selectedItems?: string[] // Array of topic IDs to display
  customOrder?: string[] // Custom ordering of topic IDs
  topicData?: MiniTopic[] // Serialized topic data for static reconstruction
  selectedTopicId?: string // The parent topic ID to get subtopics from
}

export const FolderContentsSpec = {
  group: 'block',
  atom: true,
  selectable: true,
  draggable: true,
  attrs: {
    id: { default: nanoid(10) },
    layout: { default: null },
    showIcons: { default: false },
    showDescriptions: { default: false },
    selectedItems: { default: null },
    customOrder: { default: null },
    topicData: { default: null },
  },
  toDOM() { return ['div', { 'data-folder-contents': '' }, 0] as [string, Record<string, any>, 0]; },
  parseDOM: [{ tag: 'div[data-folder-contents]' }],
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    folderContents: {
      /**
       * Insert folder contents
       */
      insertFolderContents: (
        attributes?: FolderContentsAttributes
      ) => ReturnType
      /**
       * Update folder contents attributes
       */
      updateFolderContents: (
        attributes: Partial<FolderContentsAttributes>
      ) => ReturnType
    }
  }
}

export const FolderContentsExtension = Node.create({
  name: 'FolderContents',

  group: 'block',

  content: '',

  atom: true,

  isolating: true,

  defining: true,

  addAttributes() {
    return {
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-dg-id'),
        renderHTML: (attributes) => {
          if (!attributes.id) {
            return {}
          }
          return {
            'data-dg-id': attributes.id,
          }
        },
      },
      layout: {
        default: 'grid-2',
        parseHTML: (element) => {
          const layout = element.getAttribute('data-layout')
          return layout || 'grid-2'
        },
        renderHTML: (attributes) => {
          if (!attributes.layout) {
            return {}
          }
          return {
            'data-layout': attributes.layout,
          }
        },
      },
      showIcons: {
        default: true,
        parseHTML: (element) => {
          const showIcons = element.getAttribute('data-show-icons')
          return showIcons === null ? true : showIcons === 'true'
        },
        renderHTML: (attributes) => {
          if (attributes.showIcons === undefined) {
            return {}
          }
          return {
            'data-show-icons': attributes.showIcons,
          }
        },
      },
      showDescriptions: {
        default: true,
        parseHTML: (element) => {
          const showDescriptions = element.getAttribute(
            'data-show-descriptions'
          )
          return showDescriptions === null ? true : showDescriptions === 'true'
        },
        renderHTML: (attributes) => {
          if (attributes.showDescriptions === undefined) {
            return {}
          }
          return {
            'data-show-descriptions': attributes.showDescriptions,
          }
        },
      },
      selectedItems: {
        default: [],
        parseHTML: (element) => {
          const selectedItems = element.getAttribute('data-selected-items')
          return selectedItems ? JSON.parse(selectedItems) : []
        },
        renderHTML: (attributes) => {
          if (
            !attributes.selectedItems ||
            attributes.selectedItems.length === 0
          ) {
            return {}
          }
          return {
            'data-selected-items': JSON.stringify(attributes.selectedItems),
          }
        },
      },
      customOrder: {
        default: [],
        parseHTML: (element) => {
          const customOrder = element.getAttribute('data-custom-order')
          return customOrder ? JSON.parse(customOrder) : []
        },
        renderHTML: (attributes) => {
          if (!attributes.customOrder || attributes.customOrder.length === 0) {
            return {}
          }
          return {
            'data-custom-order': JSON.stringify(attributes.customOrder),
          }
        },
      },
      topicData: {
        default: [],
        parseHTML: (element) => {
          const topicData = element.getAttribute('data-topic-data')
          return topicData ? JSON.parse(topicData) : []
        },
        renderHTML: (attributes) => {
          if (!attributes.topicData || attributes.topicData.length === 0) {
            return {}
          }
          return {
            'data-topic-data': JSON.stringify(attributes.topicData),
          }
        },
      },
      selectedTopicId: {
        default: '',
        parseHTML: (element) => {
          return element.getAttribute('data-selected-topic-id') || ''
        },
        renderHTML: (attributes) => {
          if (!attributes.selectedTopicId) {
            return {}
          }
          return {
            'data-selected-topic-id': attributes.selectedTopicId,
          }
        },
      },
    }
  },

  parseHTML() {
    return [
      {
        tag: 'div[data-type="FolderContents"]',
      },
      {
        tag: 'div.folder-contents-container',
      },
    ]
  },

  renderHTML({ HTMLAttributes }) {
    return [
      'div',
      mergeAttributes(HTMLAttributes, {
        'data-type': 'FolderContents',
        class: 'group/folder-contents relative p-0',
      }),
    ]
  },

  addNodeView() {
    return ReactNodeViewRenderer(FolderContentsNodeView)
  },

  addCommands() {
    return {
      insertFolderContents:
        (attributes = {}) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              layout: 'grid-2',
              showIcons: true,
              showDescriptions: true,
              selectedItems: [],
              customOrder: [],
              topicData: [],
              selectedTopicId: '',
              ...attributes,
            },
          })
        },
      updateFolderContents:
        (attributes) =>
        ({ commands }) => {
          return commands.updateAttributes(this.name, attributes)
        },
    }
  },

  addKeyboardShortcuts() {
    return {
      // Prevent deleting the folder contents with backspace when cursor is at the beginning
      Backspace: ({ editor }) => {
        const { selection, doc } = editor.state
        const { $from } = selection

        // Check if we're at the beginning of folder contents
        if ($from.pos === 1) return false

        const node = $from.node()
        if (node.type.name === this.name && $from.parentOffset === 0) {
          return true // Prevent deletion
        }

        return false
      },
    }
  },
})
